import fractionToInt from "./fractionToInt";
import { UnitObj } from "./quoteCartStorage";

export interface LineItem {
  configImg?: string;
  configImgRight?: string;
  exactConfigRight?: string;
  exactConfig?: string;
  doorType?: string;
  material?: string;
  swingDirection?: string;
  dl?: string;
  id?: string;
  stileCategory?: string;
  handle?: string;
  glass?: string;
  handleHeight?: string;
  doorHeight?: number;
  spacer?: string;
  wood?: string;
  screenColor?: string;
  breatherTubes?: string;
  kerf?: string;
  cladColor?: string;
  customCladColor?: string;
  comboWIndowConfig?: string;
  comboUnitLeft?: string;
  comboUnitRight?: string;
}

const noHandle: string[] = [
  "2L",
  "2R",
  "2L2R",
  "4L",
  "4R",
  "6L",
  "6R",
  "4L2R",
  "2L4R",
  "8L",
  "8R",
  "6L2R",
  "2L6R",
  "4L4R",
];

function addLineItem(cart: UnitObj[], addedFields: Set<string>): LineItem[] {
  let arr: LineItem[] = [];

  cart.forEach((unit) => {
    const isCorner = unit.doorType.includes("Corner");
    const isCombo = unit.doorType.includes("Combo");

    let obj = {
      configImg: unit.exactConfigImg,
      configImgRight: isCorner || isCombo ? unit.exactConfigImgRightCorner : "",
      exactConfigRight: isCorner ? unit.cornerConfigRight : "",
      exactConfig: unit.exactConfig,
      doorType: unit.doorType,
      material: unit.material,
      swingDirection: unit.swingDirection || "",
      dl: unit.addDL || "",
      id: unit._id,
      stileCategory: unit.stileCategory,
    };

    if(isCombo){
      obj["comboWindowConfig"] = unit.comboWindowConfig || "";
      obj["comboUnitLeft"] = unit.comboUnitLeft || "";
      obj["comboUnitRight"] = unit.comboUnitRight || "";
    }

    addedFields.forEach((field) => {
      let secondaryObj = {};

      switch (field) {
        case "Glass":
          secondaryObj["glass"] = unit.glassType || "None";
          break;
        case "Handle":
          secondaryObj["handle"] =
            isCorner &&
            noHandle.includes(unit.exactConfig) &&
            noHandle.includes(unit.cornerConfigRight || "")
              ? "N/A"
              : !isCorner &&
                (noHandle.includes(unit.exactConfig) ||
                  unit.doorType === "Bi-fold Window")
              ? "N/A"
              : unit.doorType === "Pivot" && unit.handleStyle
              ? unit.handleStyle
              : unit.doorType !== "Slider" &&
                unit.withHandle === "Add Handle Set"
              ? unit.handleStyle
              : unit.handleStyle && unit.doorType === "Slider"
              ? unit.handleStyle
              : "None";
          break;
        case "Handle Height":
          const handleHeight = unit.handleHeight || '36"';

          const updateObj = {
            handleHeight:
              isCorner &&
              noHandle.includes(unit.exactConfig) &&
              noHandle.includes(unit.cornerConfigRight || "")
                ? "N/A"
                : !isCorner &&
                  (noHandle.includes(unit.exactConfig) ||
                    unit.doorType === "Bi-fold Window")
                ? "N/A"
                : unit.doorType === "Pivot"
                ? handleHeight
                : unit.doorType !== "Slider" &&
                  unit.withHandle === "Add Handle Set"
                ? handleHeight
                : unit.handleStyle && unit.doorType === "Slider"
                ? handleHeight
                : "None",
            doorHeight: fractionToInt(unit.height, unit.heightFraction),
          };

          Object.assign(secondaryObj, updateObj);
          break;
        case "Spacer":
          secondaryObj["spacer"] = unit.spacerColor || "None";
          break;
        case "Wood":
          secondaryObj["wood"] = unit.woodAbbrev || "None";
          break;
        case "Screen Color":
          secondaryObj["screenColor"] =
            !unit.addScreen && !unit.addSlidingScreen
              ? "None"
              : unit.screenColor;
          break;
        case "Breather Tubes":
          secondaryObj["breatherTubes"] = unit.breatherTubes || "None";
          break;
        case "Kerf":
          secondaryObj["kerf"] = !unit.kerf
            ? "None"
            : unit.kerf && unit.kerfConfig?.includes("Custom")
            ? unit.kerfSetback
            : "3/8";
          break;
        case "Clad Color":
          secondaryObj["cladColor"] =
            unit.exteriorCladColor && unit.material.includes("Clad")
              ? unit.exteriorCladColor
              : "None";
          break;
        case "Custom Clad Color":
          secondaryObj["customCladColor"] =
            unit.exteriorCladColor &&
            unit.exteriorCladColor.includes("Custom") &&
            unit.material.includes("Clad") &&
            unit.customCladColor
              ? unit.customCladColor
              : "";
          break;
        default:
          console.log("No Matching Field");
      }

      Object.assign(obj, secondaryObj);
    });

    arr.push(obj);
  });

  return arr;
}

export default function checkCart(cart: UnitObj[]) {

  if (!cart.length || cart.length <= 1) return [];

  let handle: string = "";
  let alumHandle = ""
  let glass: string = cart[0].glassType || "";
  let spacer: string = cart[0].spacerColor || "";
  // set `wood` to the first unit in the cart that has a woodSpecies defined
  const firstWoodUnit = cart.find((u) => Boolean(u.woodSpecies));
  let wood: string = firstWoodUnit ? firstWoodUnit.woodSpecies : "";
  let screenColor: string = "";
  let breatherTubes: string = cart[0].breatherTubes || "None";
  let cladColor: string =
    cart[0].material === "Clad Exterior/Wood Interior" &&
    cart[0].exteriorCladColor
      ? cart[0].exteriorCladColor
      : "";
  let handleHeight: string = cart[0].handleHeight || '36"';
  let kerf: string = "";
  let kerfLoop: boolean = false;

  let kerfSelections: string[] = [];

  let allUnitCladColors: string[] = [];
  let allCustomCladColors: string[] = [];

  let fieldsSet = new Set<string>();

  // count how many units actually have a woodSpecies defined
  // if only one unit has a woodSpecies, we don't want to add the "Wood" field
  const woodCount = cart.filter((u) => Boolean(u.woodSpecies)).length;

  for (let i = 0; i < cart.length; i++) {
    const unit: UnitObj = cart[i];

    if (unit.glassType !== glass) {
      fieldsSet.add("Glass");
    }

    if (
      (unit.doorType === "Slider" && unit.sliderPocket !== "With Pocket") ||
      (unit.doorType === "Bi-fold Zero Corner" &&
        unit.withHandle === "Add Handle Set" &&
        (!noHandle.includes(unit.exactConfig) ||
          !noHandle.includes(unit.cornerConfigRight || ""))) ||
      (unit.withHandle === "Add Handle Set" &&
        !noHandle.includes(unit.exactConfig) &&
        unit.doorType !== "Bi-fold Window" &&
        unit.doorType !== "Bi-fold Zero Corner")
    ) {
      if(unit.material === "All Aluminum"){
        if (!alumHandle) alumHandle = unit.handleStyle || "";

        if (unit.handleStyle !== alumHandle) {
          fieldsSet.add("Handle");
        }
      }else{
        if (!handle) handle = unit.handleStyle || "";

        if (unit.handleStyle !== handle) {
          fieldsSet.add("Handle");
        }
      }

      const unitHandleHeight: string = unit.handleHeight || '36"';

      if (unitHandleHeight !== handleHeight) {
        fieldsSet.add("Handle Height");
      }
    }

    if (unit.doorType === "Pivot" || unit.sliderPocket === "With Pocket") {
      const unitHandleHeight: string = unit.handleHeight || '36"';

      if (unitHandleHeight !== handleHeight) {
        fieldsSet.add("Handle Height");
      }
    }

    if (unit.spacerColor !== spacer) {
      fieldsSet.add("Spacer");
    }

    if (woodCount > 1 && unit.material !== "All Aluminum" && unit.woodSpecies !== wood) {
      fieldsSet.add("Wood");
    }

    if (unit.addScreen || unit.addSlidingScreen) {
      if (!screenColor) screenColor = unit.screenColor || "";

      if (unit.screenColor !== screenColor) {
        fieldsSet.add("Screen Color");
      }
    }

    if (unit.kerf && unit.kerfConfig) {
      if (!unit.kerfConfig.includes("Custom") && !kerf) {
        kerf = "3/8";
      } else if (!kerf) {
        kerf = unit.kerfSetback || "";
      }

      if (!kerfLoop) {
        if (
          (!unit.kerfConfig.includes("Custom") && kerf !== "3/8") ||
          (unit.kerfSetback && kerf !== unit.kerfSetback)
        ) {
          kerfLoop = true;

          for (let q = 0; q < cart.length; q++) {
            const itm = cart[q];

            const hasKerf = itm.kerf && itm.kerfConfig;

            kerfSelections.push(
              hasKerf && !itm.kerfConfig?.includes("Custom")
                ? "3/8"
                : hasKerf && itm.kerfSetback
                ? itm.kerfSetback
                : "3/8"
            );
          }
        }
      }
    }

    if (
      i + 1 === cart.length &&
      kerfSelections.length &&
      !kerfSelections.every((k) => k === kerfSelections[0])
    ) {
      fieldsSet.add("Kerf");
    }

    const bt: string = unit.breatherTubes || "None";

    if (bt !== breatherTubes) {
      fieldsSet.add("Breather Tubes");
    }

    if (unit.exteriorCladColor && unit.material.includes("Clad")) {
      const cladKey = "Clad Color";

      allUnitCladColors.push(unit.exteriorCladColor);
      if (unit.exteriorCladColor.includes("Custom") && unit.customCladColor)
        allCustomCladColors.push(unit.customCladColor);

      if (!cladColor) cladColor = unit.exteriorCladColor;

      if (
        i + 1 === cart.length &&
        allUnitCladColors.every((color) => color && color.includes("Custom"))
      ) {
        if (!compareCustomClad(allCustomCladColors)) {
          fieldsSet.add(cladKey);
          fieldsSet.add(`Custom ${cladKey}`);
        }
      } else if (
        i + 1 === cart.length &&
        allUnitCladColors.includes("Custom")
      ) {
        fieldsSet.add(cladKey);
        fieldsSet.add(`Custom ${cladKey}`);
      } else {
        if (unit.exteriorCladColor !== cladColor) {
          fieldsSet.add(cladKey);
          fieldsSet.add(`Custom ${cladKey}`);
        }
      }
    }
  }

  return addLineItem(cart, fieldsSet);
}

function parseCladColor(clad: string): string {
  return clad?.replace(/-/g, "").replace(/ /g, "").toLowerCase().trim();
}

function compareCustomClad(cladArray: string[]): boolean {
  const areEqual = cladArray.every(
    (color) => parseCladColor(color) === parseCladColor(cladArray[0])
  );
  return areEqual;
}
